import {ErrorMessage, FieldArray, Form, Formik} from 'formik'
import {isEmpty} from 'lodash'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import axios from 'axios'
import * as Yup from 'yup'
import {safeText} from 'src/cms/helpers/validation/safeText'

import {StateParamsModel} from 'src/app/modules/common/Model'
import * as subsidiaryPage from '../index'
import * as subsidiaryCategory from '../../subsidiaryCategory'

import Modal from 'rsuite/Modal'

import FormRadio from 'src/cms/helpers/components/forms/FormRadio'
import FormSelect from 'src/cms/helpers/components/forms/FormSelect'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import FormInputMediaManager from 'src/cms/helpers/components/forms/FormInputMediaManager'
import {imageBaseUrl} from 'src/cms/helpers/constants'

type Props = {
  open: boolean
  params?: StateParamsModel
  handleClose: () => void
  actionType: string
  editSelectedData?: any
}

const API_URL = window.__RUNTIME_CONFIG__.REACT_APP_API_URL

const CONTENT_TYPE_LABELS: Record<string, string> = {
  'subsidiary-hero': 'Subsidiary Hero',
  'subsidiary-about': 'Subsidiary About',
  'subsidiary-highlights': 'Subsidiary Key Highlights',
  'subsidiary-solutions': 'Subsidiary Products & Services',
  'subsidiary-contact': 'Subsidiary Contact',
  'subsidiary-vision-mission': 'Subsidiary Vision & Mission',
  'subsidiary-cta': 'Subsidiary CTA',
}

const FORM_VALIDATION = Yup.object().shape({
  subsidiaryCategoryId: Yup.string().required('Subsidiary category is required'),
  layoutManagerId: Yup.string().required('Content type is required'),
  status: Yup.string().required('Status is required'),
  sectionLabel: safeText().nullable(),
  heading: safeText().nullable(),
  description: safeText(5000).nullable(),
  button1Text: safeText().nullable(),
  button2Text: safeText().nullable(),
  services: Yup.array()
    .of(
      Yup.object().shape({
        title: safeText().nullable(),
        description: safeText(5000).nullable(),
      })
    )
    .nullable(),
})

const emptyService = {title: '', description: '', image: '', link: '', order: 1}

const AddSubsidiaryPage = ({open, params, handleClose, actionType, editSelectedData}: Props) => {
  const dispatch = useDispatch()
  const isEditMode = !isEmpty(editSelectedData)
  const {loading, success} = useSelector((state: any) => state.subsidiaryPage)
  const {subsidiaryCategoryList} = useSelector((state: any) => state.subsidiaryCategory)
  const [layoutOptions, setLayoutOptions] = useState<any[]>([])

  const statusOptions = [
    {label: 'Active', value: 'Active'},
    {label: 'Inactive', value: 'Inactive'},
  ]

  useEffect(() => {
    dispatch(subsidiaryCategory.actions.getAllSubsidiaryCategory())
    axios
      .get(`${API_URL}/home-page-component`, {params: {page: 1, limit: 200}})
      .then((res) => setLayoutOptions(res?.data?.data?.components ?? []))
      .catch(() => setLayoutOptions([]))
  }, [])

  useEffect(() => {
    if (success) {
      dispatch(subsidiaryPage?.actions.getSubsidiaryPage(params))
      isEmpty(editSelectedData)
        ? toast.success('Subsidiary manager entry added successfully')
        : toast.success('Subsidiary manager entry edited successfully')
      dispatch(subsidiaryPage?.actions?.resetSubsidiaryPage())
      handleClose()
    }
  }, [success])

  const categoryOptions = (subsidiaryCategoryList?.subsidiaryCategory ?? []).map((c: any) => ({
    label: c.name,
    value: c.id,
  }))

  const contentTypeOptions = layoutOptions
    .filter((lm: any) => typeof lm.title === 'string' && lm.title.startsWith('subsidiary-'))
    .map((lm: any) => ({label: CONTENT_TYPE_LABELS[lm.title] ?? lm.title, value: lm.id}))

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{actionType} Subsidiary Manager</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{
              id: '',
              subsidiaryCategoryId: '',
              layoutManagerId: '',
              status: '',
              sectionLabel: '',
              heading: '',
              description: '',
              image: '',
              button1Text: '',
              button1Url: '',
              button2Text: '',
              button2Url: '',
              services: [emptyService],
            }}
            validationSchema={FORM_VALIDATION}
            onSubmit={(values) => {
              const formData = {...values, status: values?.status === 'Active'}
              if (!isEmpty(editSelectedData)) {
                dispatch(subsidiaryPage.actions.updateSubsidiaryPage(formData, editSelectedData?.id))
              } else {
                dispatch(subsidiaryPage.actions.addSubsidiaryPage(formData))
              }
            }}
          >
            {({touched, handleChange, errors, values, setFieldValue}) => {
              useEffect(() => {
                if (!isEmpty(editSelectedData)) {
                  const fields = [
                    'id',
                    'subsidiaryCategoryId',
                    'layoutManagerId',
                    'sectionLabel',
                    'heading',
                    'description',
                    'image',
                    'button1Text',
                    'button1Url',
                    'button2Text',
                    'button2Url',
                  ]
                  fields.forEach((f) => setFieldValue(f, editSelectedData[f] ?? '', false))
                  setFieldValue('status', editSelectedData?.status === true ? 'Active' : 'Inactive', false)
                  setFieldValue(
                    'services',
                    Array.isArray(editSelectedData?.services) && editSelectedData.services.length
                      ? editSelectedData.services
                      : [emptyService],
                    false
                  )
                }
              }, [])

              const text = (
                name: string,
                label: string,
                container = 'col-md-6 col-xs-12',
                as: 'input' | 'textarea' = 'input'
              ) => (
                <div className={container}>
                  <FormTextBox
                    type='text'
                    as={as}
                    {...(as === 'textarea' && {rows: 3})}
                    labelClassName='col-md-12'
                    containerClassName='col-md-12'
                    placeholder={label}
                    label={label}
                    name={name}
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                  />
                </div>
              )

              return (
                <Form>
                  <div className='card-body border-top pt-5'>
                    <h4 className='mb-4'>Basic Information</h4>
                    <div className='row'>
                      <div className='col-md-6 col-xs-12'>
                        <FormSelect
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          placeholder='Select category'
                          label='Subsidiary Category'
                          name='subsidiaryCategoryId'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          options={categoryOptions}
                          values={values}
                          setFieldValue={setFieldValue}
                          disabled={isEditMode}
                          required
                        />
                      </div>
                      <div className='col-md-6 col-xs-12'>
                        <FormSelect
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          placeholder='Select content type'
                          label='Content Type'
                          name='layoutManagerId'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          options={contentTypeOptions}
                          values={values}
                          setFieldValue={setFieldValue}
                          disabled={isEditMode}
                          required
                        />
                      </div>
                      <div className='col-md-6 col-xs-12'>
                        <label className='form-label fw-bolder text-dark fs-6 required'>Status</label>
                        <div className='d-flex ms-5'>
                          {statusOptions.map((s) => (
                            <FormRadio
                              key={s.value}
                              containerClassName=''
                              label='Select Status'
                              name='status'
                              type='radio'
                              onChange={handleChange}
                              errors={errors}
                              touched={touched}
                              required
                              checkBoxText={s.label}
                              value={s.value}
                            />
                          ))}
                        </div>
                        <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                          <ErrorMessage name='status' component='div' className='field-error-message' />
                        </div>
                      </div>
                    </div>

                    <h4 className='mt-6 mb-4'>Content</h4>
                    <div className='row'>
                      {text('sectionLabel', 'Section Label')}
                      {text('heading', 'Heading')}
                      <div className='col-md-12 col-xs-12'>
                        <FormTextBox
                          as='textarea'
                          rows={6}
                          type='text'
                          label='Description'
                          name='description'
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          placeholder='Description'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                        />
                      </div>
                      <div className='col-md-6 col-xs-12'>
                        <FormInputMediaManager
                          label='Background Image'
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          name='image'
                          setFieldValue={setFieldValue}
                          value={values.image}
                          note='Recommended landscape image'
                        />
                        {!isEmpty(values.image) && (
                          <div className='thumbImageBlock mt-2' style={{position: 'relative', maxWidth: 220}}>
                            <button
                              type='button'
                              title='Remove'
                              className='btn thumbImage-remove btn-primary'
                              onClick={() => setFieldValue('image', '')}
                            >
                              X
                            </button>
                            <img className='thumbImage w-100' src={`${imageBaseUrl}/${values.image}`} alt='' />
                          </div>
                        )}
                      </div>
                      {text('button1Text', 'Button 1 Text')}
                      {text('button1Url', 'Button 1 URL')}
                      {text('button2Text', 'Button 2 Text')}
                      {text('button2Url', 'Button 2 URL')}
                    </div>

                    <h4 className='mt-6 mb-4'>Services</h4>
                    <FieldArray
                      name='services'
                      render={(ah) => (
                        <div>
                          {values.services.map((_: any, i: number) => (
                            <div className='row border-bottom pb-2 mb-2' key={i}>
                              {text(`services.${i}.title`, 'Title')}
                              {text(`services.${i}.description`, 'Description', 'col-md-12 col-xs-12', 'textarea')}
                              <div className='col-md-6 col-xs-12'>
                                <FormInputMediaManager
                                  label='Image'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  name={`services.${i}.image`}
                                  setFieldValue={setFieldValue}
                                  value={values.services[i].image}
                                />
                                {!isEmpty(values.services[i].image) && (
                                  <div
                                    className='thumbImageBlock mt-2'
                                    style={{position: 'relative', maxWidth: 160}}
                                  >
                                    <button
                                      type='button'
                                      title='Remove'
                                      className='btn thumbImage-remove btn-primary'
                                      onClick={() => setFieldValue(`services.${i}.image`, '')}
                                    >
                                      X
                                    </button>
                                    <img
                                      className='thumbImage w-100'
                                      src={`${imageBaseUrl}/${values.services[i].image}`}
                                      alt=''
                                    />
                                  </div>
                                )}
                              </div>
                              {text(`services.${i}.link`, 'Link')}
                              {values.services.length > 1 && (
                                <div className='col-md-12 text-end mb-2'>
                                  <button
                                    type='button'
                                    className='btn btn-secondary btn-sm'
                                    onClick={() => ah.remove(i)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                          <div className='d-flex justify-content-end'>
                            <button
                              type='button'
                              className='btn btn-primary btn-sm w-100'
                              onClick={() => ah.push({...emptyService, order: values.services.length + 1})}
                            >
                              + Add More
                            </button>
                          </div>
                        </div>
                      )}
                    />
                  </div>

                  <div className='d-flex justify-content-end px-9'>
                    <button
                      type='submit'
                      disabled={loading}
                      className='btn btn-primary btn-sm ms-3'
                      data-cy='modal-submit'
                    >
                      Save
                    </button>
                    <button
                      type='button'
                      onClick={handleClose}
                      className='btn btn-secondary btn-sm ms-3'
                      data-cy='modal-cancel'
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    </div>
  )
}
export default AddSubsidiaryPage
